/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth }  from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { usersAPI, postsAPI } from '../utils/api';
import { getAvatarUrl, formatRelativeTime, formatCount } from '../utils/helpers';
import {
  MapPin, Link as LinkIcon, Calendar, Edit3, Camera,
  MessageCircle, UserPlus, UserCheck, MoreHorizontal,
  Grid, FileText, Image as ImageIcon, Users, X, Check
} from 'lucide-react';
import './ProfilePage.css';

const TABS = [
  { key:'posts', label:'Posts', icon:Grid },
  { key:'media', label:'Media', icon:ImageIcon },
  { key:'docs',  label:'Documents', icon:FileText },
  { key:'communities', label:'Communities', icon:Users },
];

export default function ProfilePage() {
  const { userId }  = useParams();
  const { user:me, updateUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const fileRef  = useRef(null);
  const bannerRef = useRef(null);

  const isOwn = !userId || userId === (me?._id||me?.id);
  const [profile,   setProfile]   = useState(isOwn ? me : null);
  const [posts,     setPosts]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState('posts');
  const [editing,   setEditing]   = useState(false);
  const [following, setFollowing] = useState(false);
  const [editData,  setEditData]  = useState({});

  useEffect(() => {
    const targetId = userId || me?._id || me?.id;
    if (!targetId) return;
    setLoading(true);
    Promise.all([
      isOwn ? Promise.resolve({data:me}) : usersAPI.getById(targetId).catch(()=>({data:null})),
      postsAPI.getUserPosts(targetId).catch(()=>({data:[]})),
    ]).then(([u, p]) => {
      setProfile(u.data);
      setPosts(p.data || []);
      if (u.data) {
        setFollowing((u.data.followers||[]).some(f => (f._id||f)===me?._id||(f._id||f)===me?.id));
        setEditData({ displayName:u.data.displayName, bio:u.data.bio||'', location:u.data.location||'', website:u.data.website||'' });
      }
    }).finally(() => setLoading(false));
  }, [userId, me]);

  const handleFollow = async () => {
    try {
      const { data } = await usersAPI.follow(profile._id||profile.id);
      setFollowing(data.following);
      setProfile(p => ({...p, followers: data.following ? [...(p.followers||[]),me] : (p.followers||[]).filter(f=>(f._id||f)!==me._id&&(f._id||f)!==me.id)}));
      toast.success(data.following ? 'Following!' : 'Unfollowed');
    } catch { toast.error('Action failed'); }
  };

  const handleAvatarUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const { data } = await usersAPI.updateProfile({ avatar: e.target.result });
        setProfile(p => ({...p, avatar:e.target.result}));
        updateUser?.(data);
        toast.success('Profile photo updated!');
      } catch { toast.error('Upload failed'); }
    };
    reader.readAsDataURL(file);
  };

  const handleBannerUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        await usersAPI.updateProfile({ banner: e.target.result });
        setProfile(p => ({...p, banner:e.target.result}));
        toast.success('Cover photo updated!');
      } catch { toast.error('Upload failed'); }
    };
    reader.readAsDataURL(file);
  };

  const saveEdit = async () => {
    try {
      const { data } = await usersAPI.updateProfile(editData);
      setProfile(p => ({...p, ...editData}));
      updateUser?.(data);
      setEditing(false);
      toast.success('Profile updated!');
    } catch { toast.error('Update failed'); }
  };

  // Media files from posts
  const mediaFiles = posts.flatMap(p => (p.mediaUrls||[]).map(url => ({url, post:p})));

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="loader" style={{width:32,height:32,borderWidth:3}}/>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="empty-state" style={{flex:1}}>
        <Users size={48}/>
        <h3>User not found</h3>
        <p>This profile doesn't exist or has been removed</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-scroll">

        {/* ── Cover photo ── */}
        <div className="profile-cover" style={profile.banner ? {backgroundImage:`url(${profile.banner})`} : {}}>
          {isOwn && (
            <button className="profile-cover-edit" onClick={()=>bannerRef.current?.click()}>
              <Camera size={15}/> Edit cover
            </button>
          )}
          <input ref={bannerRef} type="file" accept="image/*" style={{display:'none'}}
            onChange={e=>handleBannerUpload(e.target.files[0])}/>
        </div>

        {/* ── Header ── */}
        <div className="profile-header">
          <div className="profile-avatar-section">
            <div className="profile-avatar-wrap">
              <img src={getAvatarUrl(profile)} alt="" className="profile-avatar-img"/>
              {isOwn && (
                <button className="profile-avatar-edit" onClick={()=>fileRef.current?.click()}>
                  <Camera size={14}/>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}}
                onChange={e=>handleAvatarUpload(e.target.files[0])}/>
            </div>

            <div className="profile-actions">
              {isOwn ? (
                <button className="btn btn-secondary btn-sm" onClick={()=>setEditing(true)}>
                  <Edit3 size={14}/> Edit Profile
                </button>
              ) : (
                <>
                  <button className={`btn btn-sm ${following?'btn-secondary':'btn-primary'}`} onClick={handleFollow}>
                    {following ? <><UserCheck size={14}/> Following</> : <><UserPlus size={14}/> Follow</>}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={()=>navigate(`/chat/${profile._id||profile.id}`)}>
                    <MessageCircle size={14}/> Message
                  </button>
                  <button className="btn-icon" style={{background:'var(--bg-600)',border:'1px solid var(--border-2)'}}><MoreHorizontal size={16}/></button>
                </>
              )}
            </div>
          </div>

          {/* Name & info */}
          <div className="profile-info">
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <h1>{profile.displayName}</h1>
              {profile.isVerified && <span className="verified-badge" style={{width:18,height:18,fontSize:'.65rem'}}>✓</span>}
            </div>
            <p className="profile-username">@{profile.username}</p>
            {profile.bio && <p className="profile-bio">{profile.bio}</p>}

            <div className="profile-meta-row">
              {profile.location && <span><MapPin size={13}/> {profile.location}</span>}
              {profile.website && <a href={profile.website.startsWith('http')?profile.website:`https://${profile.website}`} target="_blank" rel="noreferrer"><LinkIcon size={13}/> {profile.website}</a>}
              <span><Calendar size={13}/> Joined {new Date(profile.createdAt||Date.now()).toLocaleDateString('en-US',{month:'long',year:'numeric'})}</span>
            </div>

            <div className="profile-stats-row">
              <div className="profile-stat"><strong>{formatCount((profile.following||[]).length)}</strong> Following</div>
              <div className="profile-stat"><strong>{formatCount((profile.followers||[]).length)}</strong> Followers</div>
              <div className="profile-stat"><strong>{formatCount(posts.length)}</strong> Posts</div>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="profile-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} className={`profile-tab ${tab===t.key?'active':''}`} onClick={()=>setTab(t.key)}>
                <Icon size={15}/> {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Tab content ── */}
        <div className="profile-tab-content">
          {tab==='posts' && (
            posts.length===0 ? (
              <div className="empty-state">
                <FileText size={40}/>
                <h3>No posts yet</h3>
                <p>{isOwn ? 'Share your first post!' : `${profile.displayName} hasn't posted yet`}</p>
              </div>
            ) : (
              <div className="profile-posts-list">
                {posts.map(post => (
                  <div key={post._id||post.id} className="profile-post-card" onClick={()=>navigate('/feed')}>
                    <p className="profile-post-content">{post.content}</p>
                    {post.mediaUrls?.[0] && <img src={post.mediaUrls[0]} alt="" className="profile-post-img"/>}
                    <div className="profile-post-stats">
                      <span>❤️ {formatCount(post.likeCount||0)}</span>
                      <span>🔁 {formatCount(post.repostCount||0)}</span>
                      <span>💬 {formatCount(post.replyCount||0)}</span>
                      <span style={{marginLeft:'auto',color:'var(--text-500)'}}>{formatRelativeTime(post.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab==='media' && (
            mediaFiles.length===0 ? (
              <div className="empty-state">
                <ImageIcon size={40}/>
                <h3>No media yet</h3>
                <p>Photos and videos shared in posts will appear here</p>
              </div>
            ) : (
              <div className="profile-media-grid">
                {mediaFiles.map((m,i) => (
                  <img key={i} src={m.url} alt="" className="profile-media-item"/>
                ))}
              </div>
            )
          )}

          {tab==='docs' && (
            <div className="empty-state">
              <FileText size={40}/>
              <h3>No documents shared</h3>
              <p>Documents shared in chats will appear here</p>
            </div>
          )}

          {tab==='communities' && (
            <div className="empty-state">
              <Users size={40}/>
              <h3>No communities</h3>
              <p>{isOwn?'Join a community to see it here':'This user hasn\'t joined any public communities'}</p>
              {isOwn && <button className="btn btn-primary btn-sm" onClick={()=>navigate('/explore')}>Explore communities</button>}
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {editing && (
        <div className="modal-overlay" onClick={()=>setEditing(false)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button className="btn-icon" onClick={()=>setEditing(false)}><X size={18}/></button>
            </div>

            <div className="form-group">
              <label>Display Name</label>
              <input value={editData.displayName} onChange={e=>setEditData(d=>({...d,displayName:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea rows={3} maxLength={160} value={editData.bio} onChange={e=>setEditData(d=>({...d,bio:e.target.value}))} placeholder="Tell people about yourself..."/>
              <p style={{fontSize:'.7rem',color:'var(--text-500)',textAlign:'right'}}>{editData.bio?.length||0}/160</p>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input value={editData.location} onChange={e=>setEditData(d=>({...d,location:e.target.value}))} placeholder="City, Country"/>
            </div>
            <div className="form-group">
              <label>Website</label>
              <input value={editData.website} onChange={e=>setEditData(d=>({...d,website:e.target.value}))} placeholder="yourwebsite.com"/>
            </div>

            <button className="btn btn-primary w-full" onClick={saveEdit}>
              <Check size={15}/> Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}